import { useMemo, useState } from 'react';
import type { LabMarkerTVitalLabMarkerMetadataT as LabMarkerT } from '@lotushealth/lotushealth_js_sdk/schemas';

import { Input } from '@/shared/ui/shadcn/input';
import { Button } from '@/shared/ui/shadcn/button';
import { useBiomarkerSearch } from '../../model/useBiomarkerSearch';

interface MarkerSearchSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddMarker: (marker: LabMarkerT) => void;
  selectedLabId?: string | null;
}

export function MarkerSearchSheet(_props: MarkerSearchSheetProps) {
  const { open, onOpenChange, onAddMarker, selectedLabId = null } = _props;
  const [query, setQuery] = useState('');

  const biomarkerQuery = useBiomarkerSearch(
    open ? { name: query, labId: selectedLabId } : undefined
  );

  const markers = useMemo(() => biomarkerQuery.data?.markers ?? [], [biomarkerQuery.data]);

  if (!open) {
    return null;
  }

  return (
    <div className="rounded-md border bg-background p-3 text-sm text-muted-foreground">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-medium text-foreground">Add biomarkers</div>
        <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
          Close
        </Button>
      </div>

      <div className="mt-3">
        <Input
          placeholder="Search biomarkers…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="max-w-sm"
        />
      </div>

      <div className="mt-3">
        {biomarkerQuery.isLoading ? (
          <div className="text-sm text-muted-foreground">Searching biomarkers…</div>
        ) : biomarkerQuery.isError ? (
          <div className="text-sm text-destructive">
            {biomarkerQuery.error instanceof Error
              ? biomarkerQuery.error.message
              : 'Could not search biomarkers.'}
          </div>
        ) : markers.length === 0 ? (
          <div className="text-sm text-muted-foreground">
            {query.trim() === '' ? 'Enter a search term to begin.' : 'No biomarkers found.'}
          </div>
        ) : (
          <div className="space-y-2">
            {markers.map((m) => (
              <div
                key={m.id}
                className="flex items-center justify-between gap-2 rounded-md border p-2"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-foreground">{m.name}</div>
                  <div className="truncate text-xs text-muted-foreground">
                    {m.metadata.lab_info?.name ?? 'Unknown lab'}
                  </div>
                </div>
                <Button type="button" variant="secondary" size="sm" onClick={() => onAddMarker(m)}>
                  Add
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
